import {
  HomeIcon,
  CurrencyDollarIcon,
  CubeIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  Cog6ToothIcon,
  BellAlertIcon,
  UsersIcon,
  ShoppingBagIcon,
  ChartBarSquareIcon,
  RectangleStackIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";

/**
 * Valid user roles matching backend UserRole enum.
 * See: globalclean-backend/app/models/user.py
 */
export const USER_ROLES = ["admin", "manager", "staff", "customer"] as const;

/**
 * Type for user role values.
 */
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Navigation item configuration.
 * Maps navigation items to allowed roles based on RBAC visibility matrix.
 * See: docs/security.md and globalclean-backend/app/core/rbac.py
 */
export interface NavItem {
  path: string;
  /** Translation key resolved via t() from the 'common' namespace at render time */
  labelKey: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Roles that can access this navigation item */
  allowedRoles: readonly UserRole[];
}

/**
 * All navigation items with their role requirements.
 * Based on RBAC visibility matrix from Story 6-6-2, 6-6-12 (useful_commands):
 * - Dashboard: admin, manager, staff
 * - Investments: admin only
 * - Inventory: admin, manager, staff
 * - Catalog: admin, manager, staff (Story 10.5-1)
 * - Production: admin, manager, staff (Story 10.5-5)
 * - Customers: admin, manager, staff
 * - Suppliers: admin, manager, staff
 * - Orders: admin, manager, staff, customer (customer sees own orders in Epic 7)
 * - Purchases: admin, manager, staff
 * - Alerts: admin, manager, staff
 * - Users: admin, manager
 * - Settings: all roles
 */
export const allNavItems: NavItem[] = [
  {
    path: "/dashboard",
    labelKey: "nav_dashboard",
    icon: HomeIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/reports",
    labelKey: "nav_reports",
    icon: ChartBarSquareIcon,
    allowedRoles: ["admin", "manager"] as const,
  },
  {
    path: "/investments",
    labelKey: "nav_investments",
    icon: CurrencyDollarIcon,
    allowedRoles: ["admin"] as const,
  },
  {
    path: "/inventory",
    labelKey: "nav_inventory",
    icon: CubeIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/catalog",
    labelKey: "nav_items_catalog",
    icon: RectangleStackIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/production",
    labelKey: "nav_production",
    icon: BeakerIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/customers",
    labelKey: "nav_customers",
    icon: UserGroupIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/suppliers",
    labelKey: "nav_suppliers",
    icon: BuildingStorefrontIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/orders",
    labelKey: "nav_orders",
    icon: ClipboardDocumentListIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  {
    path: "/portal/products",
    labelKey: "nav_products",
    icon: ShoppingBagIcon,
    allowedRoles: ["customer"] as const,
  },
  {
    path: "/portal/orders",
    labelKey: "nav_my_orders",
    icon: ClipboardDocumentListIcon,
    allowedRoles: ["customer"] as const,
  },
  {
    path: "/purchases",
    labelKey: "nav_purchases",
    icon: ShoppingCartIcon,
    allowedRoles: ["admin", "manager"] as const,
  },
  {
    path: "/alerts",
    labelKey: "nav_alerts",
    icon: BellAlertIcon,
    allowedRoles: ["admin", "manager", "staff"] as const,
  },
  { path: "/users", labelKey: "nav_users", icon: UsersIcon, allowedRoles: ["admin", "manager"] as const },
  {
    path: "/settings",
    labelKey: "nav_settings",
    icon: Cog6ToothIcon,
    allowedRoles: ["admin", "manager", "staff", "customer"] as const,
  },
];

/**
 * Get visible navigation items for a given user role.
 * Filters navigation items based on the user's role and the RBAC visibility matrix.
 *
 * Note (Story 10-5-8): Staff users can choose between two views:
 * - Delivery view: Uses StaffLayout, only sees /staff and Settings
 * - Operations view: Uses MainLayout, sees Dashboard, Orders, Inventory, Production, Catalog,
 *   Customers, Suppliers, Alerts, Settings (no Investments, Reports, Users, Purchases)
 *
 * @param userRole - The user's role (admin, manager, staff, or customer)
 * @returns Array of navigation items visible to the user
 */
export function getVisibleNavItems(userRole: string | undefined): NavItem[] {
  if (!userRole) {
    // If no role, return only Dashboard and Settings (safe defaults)
    return allNavItems.filter((item) => item.path === "/dashboard" || item.path === "/settings");
  }

  // Validate role is a known valid role
  if (!USER_ROLES.includes(userRole as UserRole)) {
    console.warn(`Invalid user role: ${userRole}. Expected one of: ${USER_ROLES.join(", ")}`);
    // Return safe defaults for invalid roles
    return allNavItems.filter((item) => item.path === "/dashboard" || item.path === "/settings");
  }

  return allNavItems.filter((item) => item.allowedRoles.includes(userRole as UserRole));
}
